/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/best-sellers.tsx — its
 * `WidgetCard` and thumbnail-title-figure rows, fed by `GET /api/billing/reports/sales?top=5`
 * (invoicing-admin.md §7) instead of `topProducts`. The API ranks the rows (largest `lineTotal`
 * first) and cuts them to five; this renders them in the order received. The thumbnail square
 * carries the rank, because this list is a ranking; the template's star rating (no such data here)
 * becomes the invoiced value. The template's date-range picker is dropped — the period is the
 * dashboard's own last 30 days, stated in the description.
 */
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { WidgetCard } from "@steelyard/theme";
import { Text } from "rizzui";
import { TileBoundary } from "./TileBoundary";
import { EmptyTile, countOf, UnreadableWidgetCard } from "./TrendStatCard";
import type { SalesRow } from "../../lib/services/invoicing";
import { money } from "../../lib/services/ordering";
import { formatMoney } from "../../lib/format";

/** One ranked row, its name already resolved by the loader (Billing returns none for customers). */
export type TopRow = SalesRow & { href?: string };

export function TopSellersTile({
  title,
  empty,
  emptyIcon,
  period,
  resolve,
}: {
  title: string;
  empty: string;
  emptyIcon: ReactNode;
  period: string;
  resolve: Promise<TopRow[]>;
}) {
  const unreadable = (error: unknown) => (
    <UnreadableWidgetCard title={title} error={error} fallback="Could not be read — this is not the same as no sales." />
  );

  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable} fallbackClassName="h-[380px]">
      {(rows) => (
        <WidgetCard
          title={title}
          description={`${period}, by invoiced value`}
          descriptionClassName="mt-1 text-gray-500"
          className="flex flex-col"
          action={
            <Link to="/reports/sales" className="whitespace-nowrap text-sm font-medium underline">
              Sales report
            </Link>
          }
        >
          {rows.length === 0 ? (
            <EmptyTile icon={emptyIcon} title={empty}>
              The five largest by invoiced value appear here once invoices are raised.
            </EmptyTile>
          ) : (
            <ol className="mt-[18px] grid grid-cols-1 gap-4 @sm:gap-5">
              {rows.map((row, index) => (
                <li key={row.groupId ?? row.groupName} className="flex min-w-0 items-start pe-2">
                  <div className="relative me-3 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary/10 font-lexend text-sm font-semibold text-primary @sm:h-12 @sm:w-12">
                    {index + 1}
                  </div>
                  <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Text className="truncate font-lexend text-sm font-medium text-gray-900 dark:text-gray-700">
                        {row.href ? (
                          <Link to={row.href} className="hover:underline">
                            {row.groupName}
                          </Link>
                        ) : (
                          row.groupName
                        )}
                      </Text>
                      <Text className="text-gray-500">{countOf(row.invoiceCount, "invoice")}</Text>
                    </div>
                    <Text className="shrink-0 font-medium tabular-nums text-gray-900">{formatMoney(money(row.lineTotal))}</Text>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </WidgetCard>
      )}
    </TileBoundary>
  );
}
